import { useState } from "react";
import { toast } from "sonner";

import { SelectorDeFecha } from "@/components/SelectorDeFecha";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useRegistrarPagoDelAbono, useRevertirPagoDelAbono } from "@/hooks/useInformes";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import { isoDesdeFecha } from "@/lib/fechas";
import type { Cotizacion } from "@/lib/informes";

/**
 * El registro del pago del anticipo, EN LÍNEA bajo la fila del listado.
 *
 * Es la versión liviana de `ModalDePagoDelAbono` y usa las MISMAS mutaciones
 * (`useRegistrarPagoDelAbono` / `useRevertirPagoDelAbono`), así que el servidor
 * valida igual y las dos formas de registrar no pueden divergir. Lo que cambia es
 * el peso: quien recorre la cola cobrando anticipos registra el pago sin perder
 * de vista la fila ni abrir una modal. La modal sigue siendo la del editor del
 * informe.
 *
 * La fecha viene con hoy puesto, pero lo que se pide es el DÍA del pago: quien
 * registra casi nunca se entera el mismo día en que entra la transferencia.
 */
export function PanelDePagoDelAbono({
  informeId,
  numero,
  cotizacion,
  onCerrar,
}: {
  informeId: number;
  /** El N° del informe, para titular el panel. */
  numero: string;
  cotizacion: Cotizacion;
  onCerrar: () => void;
}) {
  const yaPagado = cotizacion.abono_pagado_en !== null;
  const hoy = isoDesdeFecha(new Date());

  const [fecha, setFecha] = useState(cotizacion.abono_pagado_en ?? hoy);
  const [incompleta, setIncompleta] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const registrar = useRegistrarPagoDelAbono(informeId);
  const revertir = useRevertirPagoDelAbono(informeId);
  const enVuelo =
    (registrar.isPending && !registrar.isPaused) || (revertir.isPending && !revertir.isPaused);

  function manejarError(excepcion: unknown, respaldo: string) {
    const errores = parseValidationErrors(excepcion);
    if (errores?.fecha_pago) {
      setError(errores.fecha_pago);
      return;
    }
    toast.error(getApiErrorMessage(excepcion, respaldo));
  }

  function guardar(evento: React.FormEvent) {
    evento.preventDefault();

    if (incompleta || fecha === "") {
      setError(
        incompleta
          ? "Completa la fecha con el formato dd-mm-aaaa."
          : "Indica el día en que se recibió el pago.",
      );
      return;
    }
    if (fecha > hoy) {
      setError("La fecha del pago no puede ser posterior a hoy.");
      return;
    }

    setError(null);
    registrar.mutate(fecha, {
      onSuccess: () => {
        toast.success(yaPagado ? "Pago del anticipo actualizado." : "Pago del anticipo registrado.");
        onCerrar();
      },
      onError: (excepcion) =>
        manejarError(excepcion, "No pudimos registrar el pago. Inténtalo de nuevo."),
    });
  }

  function deshacer() {
    revertir.mutate(undefined, {
      onSuccess: () => {
        toast.success("Se deshizo el registro. El anticipo vuelve a quedar pendiente.");
        onCerrar();
      },
      onError: (excepcion) =>
        manejarError(excepcion, "No pudimos deshacer el registro. Inténtalo de nuevo."),
    });
  }

  return (
    <form
      onSubmit={guardar}
      aria-label={`${yaPagado ? "Corregir" : "Registrar"} pago del anticipo de ${numero}`}
      className="bg-panel-anticipo border-panel-anticipo-borde flex flex-wrap items-start gap-x-3 gap-y-2 border-t px-[1.125rem] pt-2.5 pb-3"
    >
      <span className="text-ink self-center text-[0.78125rem] font-bold">
        {yaPagado ? "Corregir pago del anticipo" : "Registrar pago del anticipo"} · {numero}
      </span>
      <label htmlFor={`fecha-pago-${informeId}`} className="text-ink-muted self-center text-xs font-semibold">
        Fecha de pago
      </label>
      <div className="w-44">
        <SelectorDeFecha
          id={`fecha-pago-${informeId}`}
          value={fecha}
          onChange={(iso) => {
            setFecha(iso);
            setError(null);
          }}
          onTextoIncompleto={setIncompleta}
          disabled={enVuelo}
          invalido={error !== null}
        />
        {error && <p className="text-destructive mt-1 text-xs">{error}</p>}
      </div>

      <div className="ml-auto flex items-center gap-2 self-center">
        {yaPagado && (
          <Button type="button" variant="ghost" size="sm" onClick={deshacer} disabled={enVuelo}>
            Deshacer registro
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" onClick={onCerrar} disabled={enVuelo}>
          Cancelar
        </Button>
        <Button type="submit" variant="fuerte" size="sm" disabled={enVuelo}>
          {enVuelo && <Spinner />}
          Guardar pago
        </Button>
      </div>
    </form>
  );
}
